import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { downloadSickNote } from '../services/docuwareService';
import { LoadingSpinner } from './ui';
import { getErrorMessage } from '../utils/errorMessages';
import './AdminEmployeeLeaveModal.css';

const AdminEmployeeLeaveModal = ({ employee, onClose }) => {
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingSickNote, setDownloadingSickNote] = useState({});

  // Balance adjustment form state.
  // The backend (POST /api/leave/admin/balance/adjust/) gates this with
  // IsSupervisorOrAdmin + a supervision check, so any authorised viewer of
  // this modal can submit; unauthorised submissions surface as a 403.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ amount: '', type: 'add', reason: '' });
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);
  const [adjustError, setAdjustError] = useState('');
  const [adjustSuccess, setAdjustSuccess] = useState('');

  useEffect(() => {
    if (employee) {
      fetchEmployeeLeaveData();
    }
  }, [employee]);

  const fetchEmployeeLeaveData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch employee's leave balance
      const balanceResponse = await axiosInstance.get(`/api/leave/admin/balance/${employee.id}/`);
      setLeaveBalance(balanceResponse.data);
      
      // Fetch employee's leave requests
      const requestsResponse = await axiosInstance.get(`/api/leave/admin/requests/?employee=${employee.id}`);
      const allRequests = requestsResponse.data.results || requestsResponse.data;
      
      // Sort by creation date (most recent first)
      const sortedRequests = allRequests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setLeaveRequests(sortedRequests);
      
    } catch (err) {
      console.error('Error fetching employee leave data:', err);
      setError('Failed to load employee leave information');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDays = (days) => {
    const num = parseFloat(days);
    return num % 1 === 0 ? Math.floor(num) : num.toFixed(1);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#FFC107',
      'approved': '#4CAF50',
      'rejected': '#F44336',
      'cancelled': '#9E9E9E',
      'completed': '#607D8B'
    };
    return colors[status] || '#9E9E9E';
  };

  const handleDownloadSickNote = async (requestId, documentId, fileName) => {
    if (!documentId || !fileName) {
      console.error('Missing sick note document ID or name');
      return;
    }
    
    try {
      setDownloadingSickNote(prev => ({ ...prev, [requestId]: true }));
      await downloadSickNote(documentId, fileName);
    } catch (error) {
      console.error('Error downloading sick note:', error);
    } finally {
      setDownloadingSickNote(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const resetAdjustForm = () => {
    setAdjustForm({ amount: '', type: 'add', reason: '' });
    setAdjustError('');
    setAdjustSuccess('');
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    setAdjustError('');
    setAdjustSuccess('');

    const amount = parseFloat(adjustForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setAdjustError('Enter an amount greater than 0.');
      return;
    }
    // Mirror the backend's half-day-step rule so the user gets feedback
    // without a network round-trip. Backend still enforces this authoritatively.
    if (Math.round(amount * 2) !== amount * 2) {
      setAdjustError('Amount must be in increments of 0.5 days (e.g. 0.5, 1, 1.5).');
      return;
    }
    if (amount > 365) {
      setAdjustError('Amount cannot exceed 365 days in a single adjustment.');
      return;
    }
    if (!adjustForm.reason.trim()) {
      setAdjustError('A reason is required.');
      return;
    }

    // Removing days is destructive and can't be undone except by adding back.
    // Force the editor to confirm with the resulting new total in the message.
    if (adjustForm.type === 'remove' && leaveBalance) {
      const currentTotal = parseFloat(leaveBalance.total_allocated);
      const projectedTotal = Math.max(0, currentTotal - amount);
      const projectedAvailable = projectedTotal
        - parseFloat(leaveBalance.total_used || 0)
        - parseFloat(leaveBalance.total_pending || 0);
      const employeeName = employee?.display_name
        || `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()
        || 'this employee';
      const warning = projectedAvailable < 0
        ? `\n\nWARNING: This would put their available balance at ${projectedAvailable} days.`
        : '';
      const ok = window.confirm(
        `Remove ${amount} day${amount === 1 ? '' : 's'} from ${employeeName}?\n\n`
        + `Current total: ${currentTotal} days\n`
        + `New total: ${projectedTotal} days${warning}`
      );
      if (!ok) return;
    }

    try {
      setAdjustSubmitting(true);
      const response = await axiosInstance.post('/api/leave/admin/balance/adjust/', {
        employee_id: employee.id,
        adjustment_amount: amount,
        adjustment_type: adjustForm.type,
        reason: adjustForm.reason.trim(),
      });

      // Refresh balance + audit-affecting data from the server so we don't
      // drift from the canonical record (avoids local-math bugs).
      await fetchEmployeeLeaveData();
      setAdjustSuccess(
        `Balance updated. New total: ${response.data?.new_balance?.total_allocated ?? ''} days.`
      );
      setAdjustForm({ amount: '', type: 'add', reason: '' });
    } catch (err) {
      console.error('Error adjusting leave balance:', err);
      setAdjustError(getErrorMessage(err, 'Unable to adjust leave balance.'));
    } finally {
      setAdjustSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-employee-leave-modal-overlay">
        <div className="admin-employee-leave-modal">
          <div className="admin-employee-leave-modal-header">
            <h3>Loading Leave Information...</h3>
            <button className="admin-employee-leave-close-button" onClick={onClose}>×</button>
          </div>
          <div className="admin-employee-leave-modal-body">
            <LoadingSpinner message="Loading employee leave information..." />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-employee-leave-modal-overlay">
        <div className="admin-employee-leave-modal">
          <div className="admin-employee-leave-modal-header">
            <h3>Error Loading Information</h3>
            <button className="admin-employee-leave-close-button" onClick={onClose}>×</button>
          </div>
          <div className="admin-employee-leave-modal-body">
            <div className="admin-employee-leave-error-state">
              <p className="admin-employee-leave-error-message">{error}</p>
              <button className="admin-employee-leave-btn-secondary" onClick={fetchEmployeeLeaveData}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-employee-leave-modal-overlay">
      <div className="admin-employee-leave-modal">
        <div className="admin-employee-leave-modal-header">
          <div className="admin-employee-leave-header-content">
            <h3>Leave Information</h3>
            <div className="admin-employee-leave-request-id">
              {employee?.display_name || `${employee?.first_name} ${employee?.last_name}`}
              <span className="admin-employee-leave-employee-id">ID: {employee?.hms_employee_id}</span>
            </div>
          </div>
          <button className="admin-employee-leave-close-button" onClick={onClose}>×</button>
        </div>

        <div className="admin-employee-leave-modal-body">
          {/* Employee Information - Compact */}
          <div className="admin-employee-leave-compact-employee-section">
            {employee?.avatar_photo && (
              <img 
                src={employee.avatar_photo} 
                alt="Employee Avatar"
                className="admin-employee-leave-compact-avatar"
              />
            )}
            <div className="admin-employee-leave-compact-employee-details">
              <div className="admin-employee-leave-employee-name-text">
                {employee?.display_name || `${employee?.first_name} ${employee?.last_name}`}
              </div>
              <div className="admin-employee-leave-employee-meta">
                {employee?.hms_employee_id}
                {employee?.department && ` • ${typeof employee.department === 'object' ? employee.department.name : employee.department}`}
                {employee?.office && ` • ${typeof employee.office === 'object' ? employee.office.name : employee.office}`}
              </div>
            </div>
          </div>

          {/* Leave Balance - Compact Header Section */}
          {leaveBalance && (
            <div className="admin-employee-leave-compact-header-section">
              <div className="admin-employee-leave-balance-badge">
                <span className="admin-employee-leave-balance-label">Leave Balance {new Date().getFullYear()}</span>
                <div className="admin-employee-leave-balance-stats">
                  <div className="admin-employee-leave-stat-item">
                    <span className="admin-employee-leave-stat-label">Available</span>
                    <span className="admin-employee-leave-stat-value available">{formatDays(leaveBalance.available_days)}</span>
                  </div>
                  <div className="admin-employee-leave-stat-item">
                    <span className="admin-employee-leave-stat-label">Used</span>
                    <span className="admin-employee-leave-stat-value used">{formatDays(leaveBalance.total_used)}</span>
                  </div>
                  <div className="admin-employee-leave-stat-item">
                    <span className="admin-employee-leave-stat-label">Pending</span>
                    <span className="admin-employee-leave-stat-value pending">{formatDays(leaveBalance.total_pending)}</span>
                  </div>
                  <div className="admin-employee-leave-stat-item">
                    <span className="admin-employee-leave-stat-label">Total</span>
                    <span className="admin-employee-leave-stat-value total">{formatDays(leaveBalance.total_allocated)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Adjust Leave Days */}
          {leaveBalance && (
            <div className="admin-employee-leave-adjust-section">
              {!adjustOpen ? (
                <button
                  type="button"
                  className="admin-employee-leave-adjust-toggle"
                  onClick={() => { resetAdjustForm(); setAdjustOpen(true); }}
                >
                  Adjust Leave Days
                </button>
              ) : (
                <form
                  className="admin-employee-leave-adjust-form"
                  onSubmit={handleAdjustSubmit}
                  noValidate
                >
                  <div className="admin-employee-leave-adjust-header">
                    <h4>Adjust Leave Days ({new Date().getFullYear()})</h4>
                    <button
                      type="button"
                      className="admin-employee-leave-adjust-cancel"
                      onClick={() => { setAdjustOpen(false); resetAdjustForm(); }}
                      disabled={adjustSubmitting}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="admin-employee-leave-adjust-row">
                    <div className="admin-employee-leave-adjust-field">
                      <label htmlFor="adjust-type">Action</label>
                      <select
                        id="adjust-type"
                        value={adjustForm.type}
                        onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value })}
                        disabled={adjustSubmitting}
                      >
                        <option value="add">Add days</option>
                        <option value="remove">Remove days</option>
                      </select>
                    </div>
                    <div className="admin-employee-leave-adjust-field">
                      <label htmlFor="adjust-amount">Amount</label>
                      <input
                        id="adjust-amount"
                        type="number"
                        min="0.5"
                        max="365"
                        step="0.5"
                        placeholder="0.0"
                        value={adjustForm.amount}
                        onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
                        disabled={adjustSubmitting}
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-employee-leave-adjust-field">
                    <label htmlFor="adjust-reason">Reason (required)</label>
                    <textarea
                      id="adjust-reason"
                      rows="2"
                      placeholder="e.g. Carry-over from 2024, contract correction, etc."
                      value={adjustForm.reason}
                      onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                      disabled={adjustSubmitting}
                      required
                    />
                  </div>

                  {adjustError && (
                    <div className="admin-employee-leave-adjust-error">{adjustError}</div>
                  )}
                  {adjustSuccess && (
                    <div className="admin-employee-leave-adjust-success">{adjustSuccess}</div>
                  )}

                  <button
                    type="submit"
                    className="admin-employee-leave-adjust-submit"
                    disabled={adjustSubmitting}
                  >
                    {adjustSubmitting ? 'Applying...' : 'Apply Adjustment'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Recent Leave Requests */}
          <div className="admin-employee-leave-requests-section">
            <div className="admin-employee-leave-section-header">
              <h4>Leave Request History</h4>
              <span className="admin-employee-leave-request-count">
                {leaveRequests.length} {leaveRequests.length === 1 ? 'request' : 'requests'}
              </span>
            </div>
            
            {leaveRequests.length > 0 ? (
              <div className="admin-employee-leave-requests-list">
                {leaveRequests.map((request) => (
                  <div key={request.id} className="admin-employee-leave-request-item">
                    <div className="admin-employee-leave-request-top-row">
                      <div className="admin-employee-leave-request-badges">
                        <span 
                          className="admin-employee-leave-type-badge" 
                          style={{ backgroundColor: request.leave_type_details?.color || '#4CAF50' }}
                        >
                          {request.leave_type_details?.name}
                        </span>
                        <span 
                          className="admin-employee-leave-status-badge" 
                          style={{ backgroundColor: getStatusColor(request.status) }}
                        >
                          {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                        </span>
                      </div>
                      <div className="admin-employee-leave-request-id-badge">
                        #{request.id}
                      </div>
                    </div>
                    
                    <div className="admin-employee-leave-details-grid">
                      <div className="admin-employee-leave-detail-item">
                        <div className="admin-employee-leave-detail-label">Start Date</div>
                        <div className="admin-employee-leave-detail-value">{formatDate(request.start_date)}</div>
                      </div>
                      
                      <div className="admin-employee-leave-detail-item">
                        <div className="admin-employee-leave-detail-label">End Date</div>
                        <div className="admin-employee-leave-detail-value">{formatDate(request.end_date)}</div>
                      </div>
                      
                      <div className="admin-employee-leave-detail-item">
                        <div className="admin-employee-leave-detail-label">Total Days</div>
                        <div className="admin-employee-leave-detail-value">{formatDays(request.total_days)} days</div>
                      </div>
                      
                      <div className="admin-employee-leave-detail-item">
                        <div className="admin-employee-leave-detail-label">Submitted</div>
                        <div className="admin-employee-leave-detail-value">{formatDate(request.created_at)}</div>
                      </div>
                    </div>
                    
                    {request.reason && (
                      <div className="admin-employee-leave-request-reason">
                        <div className="admin-employee-leave-detail-label">Reason</div>
                        <div className="admin-employee-leave-reason-text">{request.reason}</div>
                      </div>
                    )}

                    {/* Sick Note Display Section - For Sick Leave >= 3 consecutive days */}
                    {request.leave_type_details?.name === 'Sick Leave' && request.total_days >= 3 && (
                      <div className="admin-employee-sick-note-display-section">
                        <div className="admin-employee-leave-detail-label">Doctor's Sick Note</div>
                        {request.sick_note_id ? (
                          <div className="admin-employee-sick-note-card">
                            <div className="admin-employee-sick-note-card-header">
                              <i className="fas fa-file-medical" style={{marginRight: '8px', color: '#4CAF50'}}></i>
                              <span className="admin-employee-sick-note-card-title">Medical Certificate Attached</span>
                            </div>
                            <div className="admin-employee-sick-note-card-body">
                              <div className="admin-employee-sick-note-info-row">
                                <span className="admin-employee-sick-note-info-label">File:</span>
                                <span className="admin-employee-sick-note-info-value">{request.sick_note_name}</span>
                              </div>
                              {request.sick_note_uploaded_at && (
                                <div className="admin-employee-sick-note-info-row">
                                  <span className="admin-employee-sick-note-info-label">Uploaded:</span>
                                  <span className="admin-employee-sick-note-info-value">
                                    {new Date(request.sick_note_uploaded_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              )}
                              <div className="admin-employee-sick-note-info-row">
                                <span className="admin-employee-sick-note-info-label">Total Days:</span>
                                <span className="admin-employee-sick-note-info-value">{formatDays(request.total_days)} days</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="admin-employee-sick-note-download-btn"
                              onClick={() => handleDownloadSickNote(request.id, request.sick_note_docuware_document_id, request.sick_note_name)}
                              disabled={downloadingSickNote[request.id]}
                            >
                              <i className="fas fa-download" style={{marginRight: '6px'}}></i>
                              {downloadingSickNote[request.id] ? 'Downloading...' : 'Download Sick Note'}
                            </button>
                          </div>
                        ) : (
                          <div className="admin-employee-sick-note-card admin-employee-sick-note-missing">
                            <div className="admin-employee-sick-note-card-header">
                              <i className="fas fa-exclamation-triangle" style={{marginRight: '8px', color: '#ff9800'}}></i>
                              <span className="admin-employee-sick-note-card-title">Sick Note Required</span>
                            </div>
                            <div className="admin-employee-sick-note-card-body">
                              <div className="admin-employee-sick-note-info-row">
                                <span className="admin-employee-sick-note-info-label">Status:</span>
                                <span className="admin-employee-sick-note-info-value">Not yet uploaded</span>
                              </div>
                              <div className="admin-employee-sick-note-info-row">
                                <span className="admin-employee-sick-note-info-label">Total Days:</span>
                                <span className="admin-employee-sick-note-info-value">{formatDays(request.total_days)} days</span>
                              </div>
                              <p className="admin-employee-sick-note-warning-text">
                                A doctor's sick note is required for {Math.floor(request.total_days)} or more consecutive sick days.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {request.admin_notes && (
                      <div className="admin-employee-leave-admin-notes">
                        <div className="admin-employee-leave-detail-label">Admin Notes</div>
                        <div className="admin-employee-leave-notes-text">{request.admin_notes}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-employee-leave-no-requests">
                <div className="admin-employee-leave-no-requests-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <h4>No Leave Requests</h4>
                <p>This employee has not submitted any leave requests yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmployeeLeaveModal;

